import { Typography, Table, Button, Space, message, Empty, Tag, Tabs } from "antd";
import {
  ShareAltOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FileOutlined,
  FolderFilled,
  FolderOpenOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import useSWR from "swr";
import { FileAccessGrant, FolderAccessGrant, SharedWithMeResponse } from "@/types";
import { listSharedWithMe, downloadFile } from "@/services/storageManager";
import { formatDateOnly } from "@/utils/formatters";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

export default function SharedWithMePage() {
  const [downloading, setDownloading] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch files and folders shared with the current user
  const { data, isLoading } = useSWR<SharedWithMeResponse>(
    "/access/shared-with-me",
    listSharedWithMe,
    { revalidateOnFocus: false }
  );

  const sharedFiles = data?.files || [];
  const sharedFolders = data?.folders || [];
  const totalItems = sharedFiles.length + sharedFolders.length;

  const handleDownload = async (grant: FileAccessGrant) => {
    try {
      setDownloading(grant.id);
      await downloadFile(grant.fileId, grant.fileName);
      message.success(`Downloaded ${grant.fileName}`);
    } catch (error: any) {
      console.error("Download error:", error);
      message.error(error.message || error.response?.data?.message || "Failed to download file");
    } finally {
      setDownloading(null);
    }
  };

  const handleOpenFolder = (grant: FolderAccessGrant) => {
    // Navigate to the shared folder view
    navigate(`/share/shared-folders/${grant.folderId}`);
  };

  const renderExpiry = (date: string | null) => {
    if (!date) {
      return <Text type="secondary">Never</Text>;
    }
    const expiresAt = new Date(date);
    const now = new Date();
    const daysLeft = Math.ceil(
      (expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysLeft < 0) {
      return <Tag color="red">Expired</Tag>;
    } else if (daysLeft <= 3) {
      return (
        <Space>
          <ClockCircleOutlined style={{ color: "#faad14" }} />
          <Text type="warning">{daysLeft} days</Text>
        </Space>
      );
    } else {
      return formatDateOnly(date);
    }
  };

  const fileColumns: ColumnsType<FileAccessGrant> = [
    {
      title: "File",
      dataIndex: "fileName",
      key: "fileName",
      render: (fileName) => (
        <Space>
          <FileOutlined />
          <Text strong>{fileName}</Text>
        </Space>
      ),
    },
    {
      title: "Shared by",
      dataIndex: "grantedBy",
      key: "grantedBy",
      width: 200,
      render: (grantedBy) => (
        <Space>
          <UserOutlined />
          <Text>{grantedBy.name || grantedBy.email}</Text>
        </Space>
      ),
    },
    {
      title: "Granted",
      dataIndex: "grantedAt",
      key: "grantedAt",
      width: 120,
      render: (date) => formatDateOnly(date),
    },
    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      width: 120,
      render: renderExpiry,
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(record)}
          loading={downloading === record.id}
        >
          Download
        </Button>
      ),
    },
  ];

  const folderColumns: ColumnsType<FolderAccessGrant> = [
    {
      title: "Folder",
      dataIndex: "folderName",
      key: "folderName",
      render: (folderName, record) => (
        <Space>
          <FolderFilled style={{ color: record.folderColor || "#1890ff", fontSize: 18 }} />
          <Text strong>{folderName}</Text>
        </Space>
      ),
    },
    {
      title: "Path",
      dataIndex: "folderPath",
      key: "folderPath",
      width: 200,
      render: (path) => (
        <Text type="secondary" ellipsis style={{ maxWidth: 180 }}>
          {path || "/"}
        </Text>
      ),
    },
    {
      title: "Shared by",
      dataIndex: "grantedBy",
      key: "grantedBy",
      width: 200,
      render: (grantedBy) => (
        <Space>
          <UserOutlined />
          <Text>{grantedBy.name || grantedBy.email}</Text>
        </Space>
      ),
    },
    {
      title: "Granted",
      dataIndex: "grantedAt",
      key: "grantedAt",
      width: 120,
      render: (date) => formatDateOnly(date),
    },
    {
      title: "Expires",
      dataIndex: "expiresAt",
      key: "expiresAt",
      width: 120,
      render: renderExpiry,
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<FolderOpenOutlined />}
          onClick={() => handleOpenFolder(record)}
        >
          Open
        </Button>
      ),
    },
  ];

  if (!isLoading && totalItems === 0) {
    return (
      <div style={{ padding: "24px 0" }}>
        <Title level={4} style={{ marginBottom: 24 }}>
          <ShareAltOutlined style={{ marginRight: 8 }} />
          Shared with me
        </Title>
        <Empty
          description="Nothing has been shared with you yet"
          style={{ marginTop: 80 }}
        />
      </div>
    );
  }

  const tabItems = [
    {
      key: "all",
      label: `All (${totalItems})`,
      children: (
        <>
          {sharedFolders.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <Text strong style={{ display: "block", marginBottom: 12 }}>
                <FolderFilled style={{ marginRight: 8 }} />
                Folders ({sharedFolders.length})
              </Text>
              <Table
                columns={folderColumns}
                dataSource={sharedFolders}
                rowKey="id"
                loading={isLoading}
                pagination={false}
                size="small"
              />
            </div>
          )}
          {sharedFiles.length > 0 && (
            <div>
              <Text strong style={{ display: "block", marginBottom: 12 }}>
                <FileOutlined style={{ marginRight: 8 }} />
                Files ({sharedFiles.length})
              </Text>
              <Table
                columns={fileColumns}
                dataSource={sharedFiles}
                rowKey="id"
                loading={isLoading}
                pagination={sharedFiles.length > 10 ? { pageSize: 10 } : false}
                size="small"
              />
            </div>
          )}
        </>
      ),
    },
    {
      key: "folders",
      label: `Folders (${sharedFolders.length})`,
      children: (
        <Table
          columns={folderColumns}
          dataSource={sharedFolders}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (total) => `${total} shared folders`,
          }}
        />
      ),
    },
    {
      key: "files",
      label: `Files (${sharedFiles.length})`,
      children: (
        <Table
          columns={fileColumns}
          dataSource={sharedFiles}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (total) => `${total} shared files`,
          }}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "24px 0" }}>
      <Title level={4} style={{ marginBottom: 24 }}>
        <ShareAltOutlined style={{ marginRight: 8 }} />
        Shared with me
      </Title>

      <Tabs items={tabItems} defaultActiveKey="all" />
    </div>
  );
}
